<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import OcticonButton from '../../components/OcticonButton.vue'
import SancareOcticon from '../../components/SancareOcticon.vue'

export interface SancareHeaderMenuItem {
  icon: string
  id?: string
  isDisabled?: boolean
  label: string
  tooltip?: string
  onClick: () => void
}

export interface SancareHeaderProps {
  link?: string | RouteLocationRaw | null
  logo?: string
  menuIcon?: string
  menuItems?: { items: SancareHeaderMenuItem[] }[]
  title?: string
  subtitle?: string
}

const props = withDefaults(defineProps<SancareHeaderProps>(), {
  link: null,
  logo: undefined,
  menuIcon: 'gear',
  menuItems: () => [],
  title: undefined,
  subtitle: undefined,
})

const linkTag = computed(() => {
  if (!props.link) return 'div'
  if (typeof props.link === 'string') return 'a'
  return 'router-link'
})

const linkProps = computed(() => {
  if (!props.link) return {}

  if (typeof props.link === 'string') {
    return { href: props.link }
  }

  return { to: props.link as RouteLocationRaw }
})
</script>

<template>
  <header class="d-flex align-items-center justify-content-between px-4 py-2 sancare-header">
    <div class="sancare-header__content-left d-flex align-items-center gap-4">
      <component
        :is="linkTag"
        v-bind="linkProps"
        class="d-flex align-items-center gap-2"
      >
        <img
          v-if="logo"
          id="header-logo"
          :src="logo"
          alt="Logo"
          class="sancare-header__content-left__logo"
        >

        <div class="d-flex align-items-baseline gap-2">
          <span class="sancare-header__content-left__title">{{ title }}</span>
          <span class="sancare-header__content-left__subtitle">{{ subtitle }}</span>
        </div>
      </component>

      <slot name="content-left" />
    </div>

    <slot name="content-middle" />

    <v-dropdown
      v-if="menuItems.length > 0"
      class="sancare-header__menu"
      placement="bottom-end"
    >
      <octicon-button
        id="header-menu-button"
        :octicon-name="menuIcon"
        :width="20"
        class="sancare-header__menu__icon"
        aria-controls="header-menu-dropdown"
        aria-haspopup="menu"
      />
      <template #popper>
        <ul
          id="header-menu-dropdown"
          class="dropdown-menu sancare-header__menu__items"
          aria-labelledby="header-menu-button"
        >
          <template
            v-for="(section, sIdx) in menuItems"
            :key="sIdx"
          >
            <li class="d-flex flex-column gap-1">
              <div
                v-for="item in section.items"
                :key="item.label"
                v-tooltip="item.tooltip"
              >
                <button
                  :id="item.id"
                  v-close-popper
                  class="dropdown-item d-flex align-items-center gap-2"
                  :disabled="item.isDisabled"
                  @click="!item.isDisabled && item.onClick()"
                >
                  <sancare-octicon
                    :name="item.icon"
                    :width="20"
                    :class="{ 'sancare-header__menu__items__icon--disabled': item.isDisabled }"
                  />
                  {{ item.label }}
                </button>
              </div>
            </li>

            <div
              v-if="sIdx < menuItems.length - 1"
              class="dropdown-divider"
            />
          </template>
        </ul>
      </template>
    </v-dropdown>
  </header>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';

.sancare-header {
  background: $bg-primary-subtle;
  border-bottom: 1px solid #e5eaed;

  &__content-left {
    &__logo {
      height: 1.7rem;
    }

    &__title {
      font-family: 'Lato', sans-serif;
      font-weight: 900;
      letter-spacing: .1em;
      color: $sancare-header-blue;
      user-select: none;

      &:hover {
        text-decoration: none;
      }
    }

    &__subtitle {
      color: #8b98c1;
      font-size: .9em;
      user-select: none;
    }
  }

  &__menu {
    &__icon {
      fill: $sancare-light-purple;
      cursor: pointer;
    }

    &__items {
      padding: $space-s 0 !important;

      &__icon {
        &--disabled {
          fill: var(--bs-dropdown-link-disabled-color);
        }
      }
    }
  }
}
</style>
